import { useDatastore, DEPRECATED_useModulePublicData } from "util/datastore";
import { useEffect, useState } from "react";
import { LoadingScreen } from "component/banner";
import { Heading, Paragraph } from 'component/text';
import { View } from "react-native";
import { colorBorderPrimary, colorTextDisabled, colorTextPrimary, colorTextSecondary } from 'component/color';
import { formatSuspensions, SuspensionObject } from "open-modules/zdf/component/suspension/suspension";
import { t, undefFP } from "util/stdtypes";

export type ProfileModerationData = {
    preview: any,
    flagsReceivedCount: number,
    flagsGivenCount: number,
    commentCount: number,
    rejectedCommentCount: number,
    suspensions?: Record<string, SuspensionObject>,
    memberSince: number
}

export function useUserProfileModerationData({ userId }) : ProfileModerationData | null {
    const [userProfileData, setUserProfileData] = useState(null);
    const datastore = useDatastore();

    const timestampLastModerationAction = DEPRECATED_useModulePublicData("moderation", ["user", userId, "timeLastModerationAction"]);
    useEffect(() => {
        const fetchUserProfileData = async () => {
            if (!userId) {
                return;
            }
            const userProfileDataResult = await datastore.callServerAsync("moderationZdf", "getUserProfileData", { userKey: userId });
            if (userProfileDataResult && JSON.stringify(userProfileDataResult) !== JSON.stringify(userProfileData)) {
                setUserProfileData(userProfileDataResult);
            }
        };
        fetchUserProfileData();
    }, [timestampLastModerationAction]);

    return userProfileData;
}

function CountBox({ count, label, disabled, formatParams=undefFP }) {
    const textColorBlack = disabled ? colorTextDisabled : colorTextPrimary;
    const textColorGrey = disabled ? colorTextDisabled : colorTextSecondary;
    return (
        <View style={{ flex: 1, gap: 8 }}>
            <Heading text={count.toString()} type="large" weight="medium" color={textColorBlack} />
            <Paragraph color={textColorGrey} label={label} formatParams={formatParams} />
        </View>
    );
}

function CountBoxSeparator() {
    return <View style={{ flex: 0, borderLeftWidth: 1, borderColor: colorBorderPrimary, alignSelf: "stretch", marginHorizontal: 8 }} />;
}

export function ModDashboardUserStatistics({ userProfileData, disabled = false }: { userProfileData?: ProfileModerationData | null, disabled?: boolean}) {
    if (!userProfileData) {
        return <LoadingScreen />;
    }

    const formattedSuspensions = formatSuspensions(userProfileData?.suspensions ?? {})

    return (
        <View style={{ justifyContent: "space-between", flexDirection: "row", minWidth: 450 }}>
            <CountBox count={userProfileData.commentCount || 0} label={t("{noun}")} formatParams={{ count: userProfileData.commentCount || 0, singular: t('comment & reply'), plural: t('comments & replies') }} disabled={disabled} />
            <CountBoxSeparator />
            <CountBox count={userProfileData.rejectedCommentCount || 0} label={t("{noun}")} formatParams={{ count: userProfileData.rejectedCommentCount || 0, singular: t('rejected comment'), plural: t('rejected comments') }} disabled={disabled} />
            <CountBoxSeparator />
            <CountBox count={userProfileData.flagsGivenCount || 0} label={t("{noun}")} formatParams={{ count: userProfileData.flagsGivenCount || 0, singular: t('reported comment'), plural: t('reported comments') }} disabled={disabled} />
            <CountBoxSeparator />
            <CountBox count={userProfileData.flagsReceivedCount || 0} label={t("{noun}")} formatParams={{ count: userProfileData.flagsReceivedCount || 0, singular: t('flagged comment'), plural: t('flagged comments') }} disabled={disabled} />
            <CountBoxSeparator />
            <CountBox count={formattedSuspensions.activeWarnings?.length|| 0} label={t("{noun}")} formatParams={{ count: Object.keys(userProfileData.suspensions?.warnings || {}).length || 0, singular: t('user warning'), plural: t('user warnings') }} disabled={disabled} />
        </View>
    );
}
